/**
 * Integrations page — Manus-style grid with modal detail.
 * Dark theme, no emojis, Preact.
 */
import { useEffect, useState, useCallback, useMemo } from 'preact/hooks';
import { api } from '../api/client.js';

import { T } from '../styles/tokens';
import { timeAgoLong } from '../utils/time';
import { SkeletonBar, SearchInput, FilterChips, Badge, StatusBadge as UIStatusBadge, Button, Input, Dropdown } from '../components/ui';
import { user } from '../stores/auth.store';
import type { DropdownItem } from '../components/ui/Dropdown';

// ── Types ──
interface CatalogItem {
  id: string;
  label: string;
  category: string;
  color: string;
  svg_path: string | null;
  description: string | null;
  fields_json: string | null;
  how_to_get: string | null;
  docs_url: string | null;
  auth_type: string | null;
  scopes: string | null;
  webhook_url_template: string | null;
  website: string | null;
  fields?: { key: string; label: string; type?: string; placeholder?: string; required?: boolean }[];
}

function parseFields(item: CatalogItem): { key: string; label: string; placeholder: string }[] {
  if (item.fields && item.fields.length > 0) {
    return item.fields.map(f => ({ key: f.key, label: f.label, placeholder: f.placeholder || `Insira ${f.label}` }));
  }
  if (item.fields_json) {
    try {
      const parsed = JSON.parse(item.fields_json);
      return parsed.map((f: any) => ({ key: f.key, label: f.label, placeholder: f.placeholder || `Insira ${f.label}` }));
    } catch {}
  }
  return [{ key: 'api_key', label: 'API Key', placeholder: 'Insira a chave' }];
}

interface ConnectionInfo {
  connection_id: string;
  display_name?: string;
  keys: Record<string, { preview: string; updated_at: string }>;
}

interface AccountKeys {
  connections: ConnectionInfo[];
  keys?: Record<string, { preview: string; updated_at: string }>;
  display_name?: string;
}

interface AgentInfo {
  machine_id: string;
  agent_name: string;
  integrations: { provider: string; label: string; status: string; updated_at: string }[];
  credential_previews?: Record<string, Record<string, { preview: string; updated_at: string }>>;
  env_keys?: string[];
  last_auto_sync?: string;
  env_path?: string;
  config_path?: string;
}

interface FullData {
  catalog: CatalogItem[];
  account: Record<string, AccountKeys>;
  agents: AgentInfo[];
}

const TOKEN_PREFIXES: Record<string, RegExp> = {
  'ghp_': /^ghp_[A-Za-z0-9_]{36,}$/,
  'github_pat_': /^github_pat_[A-Za-z0-9_]{22,}$/,
  'sk-ant-': /^sk-ant-[A-Za-z0-9_-]{20,}$/,
  'sk_': /^(sk_live_|sk_test_|rk_test_|rk_live_)[A-Za-z0-9]{20,}$/,
  'SG.': /^SG\.[A-Za-z0-9_-]{20,}$/,
  're_': /^re_[A-Za-z0-9]{20,}$/,
  'whsec_': /^whsec_[A-Za-z0-9]{20,}$/,
};

function validateTokenFormat(value: string): 'valid' | 'invalid' | 'neutral' {
  if (!value || value.length < 5) return 'neutral';
  for (const [, regex] of Object.entries(TOKEN_PREFIXES)) {
    if (regex.test(value)) return 'valid';
  }
  for (const prefix of Object.keys(TOKEN_PREFIXES)) {
    if (value.startsWith(prefix)) return 'invalid';
  }
  return 'neutral';
}

function getAccountKeys(providerId: string, account: Record<string, AccountKeys>): Record<string, { preview: string; updated_at: string }> {
  const entry = account[providerId];
  if (!entry) return {};
  if (entry.connections?.length) {
    const merged: Record<string, { preview: string; updated_at: string }> = {};
    for (const conn of entry.connections) Object.assign(merged, conn.keys);
    return merged;
  }
  return entry.keys || {};
}

function getConnections(providerId: string, account: Record<string, AccountKeys>): ConnectionInfo[] {
  const entry = account[providerId];
  if (!entry) return [];
  if (entry.connections?.length) return entry.connections;
  if (entry.keys && Object.keys(entry.keys).length > 0) {
    return [{ connection_id: 'default', display_name: entry.display_name, keys: entry.keys }];
  }
  return [];
}

function getStatus(providerId: string, account: Record<string, AccountKeys>, agents: AgentInfo[]): 'connected' | 'partial' | 'disconnected' {
  const conns = getConnections(providerId, account);
  const hasAccount = conns.some(c => Object.keys(c.keys).length > 0);
  const hasAgent = agents.some(a => a.integrations.some(i => i.provider?.toLowerCase() === providerId.toLowerCase() && (i.status === 'active' || i.status === 'reported')));
  if (hasAccount && hasAgent) return 'connected';
  if (hasAccount || hasAgent) return 'partial';
  return 'disconnected';
}

// ── Skeleton Card ──
function CardSkeleton() {
  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14,
      padding: '20px 18px', display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <SkeletonBar width={44} height={44} style={{ borderRadius: 12, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <SkeletonBar width={120} height={14} />
        <SkeletonBar width={200} height={11} style={{ marginTop: 6 }} />
      </div>
    </div>
  );
}

// ── Integration Grid Card (Manus-style) ──
function IntegrationGridCard({
  item, status, agents, statusLoading, onClick, account,
}: {
  item: CatalogItem;
  status: 'connected' | 'partial' | 'disconnected';
  agents: AgentInfo[];
  statusLoading?: boolean;
  onClick: () => void;
  account?: Record<string, AccountKeys>;
}) {
  const agentsWithProvider = agents.filter(a =>
    a.integrations.some(i => i.provider?.toLowerCase() === item.id?.toLowerCase())
  );
  // Detectar se é conexão a nível de conta (tem account keys mas não agentes)
  const hasAccountKeys = account && account[item.id] && (
    (account[item.id].keys && Object.keys(account[item.id].keys || {}).length > 0) ||
    (account[item.id].connections?.length > 0)
  );
  const isAccountOnly = hasAccountKeys && agentsWithProvider.length === 0;

  return (
    <div
      onClick={onClick}
      style={{
        background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14,
        padding: '20px 18px', cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s',
        display: 'flex', alignItems: 'center', gap: 14, position: 'relative',
      }}
      onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.boxShadow = `0 0 16px ${T.accent}20`; }}
      onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Icon */}
      {item.svg_path ? (
        <div style={{
          width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `${item.color}15`, border: `1px solid ${item.color}25`, flexShrink: 0,
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill={item.color || T.text}><path d={item.svg_path} /></svg>
        </div>
      ) : (
        <div style={{
          width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `${item.color}15`, border: `1px solid ${item.color}25`, flexShrink: 0,
          fontSize: 20, fontWeight: 700, color: item.color,
        }}>
          {item.label.charAt(0)}
        </div>
      )}

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{item.label}</span>
          {status === 'connected' && (
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.success, flexShrink: 0 }} />
          )}
          {status === 'partial' && (
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.warning, flexShrink: 0 }} />
          )}
        </div>
        <div style={{
          fontSize: 12, color: T.textSecondary, marginTop: 3, lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {item.description || item.category}
        </div>
      </div>

      {/* Agent avatars (right side) — only when NO account keys */}
      {agentsWithProvider.length > 0 && !hasAccountKeys && (
        <div style={{ display: 'flex', flexShrink: 0 }}>
          {agentsWithProvider.slice(0, 3).map((a, i) => (
            <div
              key={a.machine_id}
              title={a.agent_name || a.machine_id.slice(0, 8)}
              style={{
                width: 28, height: 28, borderRadius: '50%',
                background: `hsl(${(a.agent_name || a.machine_id).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 60%, 40%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: '#fff',
                border: `2px solid ${T.bgCard}`,
                marginLeft: i > 0 ? -6 : 0,
              }}
            >
              {(a.agent_name || a.machine_id).charAt(0).toUpperCase()}
            </div>
          ))}
          {agentsWithProvider.length > 3 && (
            <div style={{
              width: 28, height: 28, borderRadius: '50%', background: T.bgCardHover,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 600, color: T.textMuted, border: `2px solid ${T.bgCard}`, marginLeft: -6,
            }}>+{agentsWithProvider.length - 3}</div>
          )}
        </div>
      )}

      {/* Account-level: show user avatar stacked with agent avatars */}
      {hasAccountKeys && agentsWithProvider.length === 0 && (
        <div style={{ display: 'flex', flexShrink: 0 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
            background: T.gradientPrimary || T.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: '#fff',
            border: `2px solid ${T.bgCard}`,
          }}>
            {(user.value?.name || user.value?.email || '?')[0].toUpperCase()}
          </div>
        </div>
      )}
      {/* Account + agents: stack user avatar with agent avatars */}
      {hasAccountKeys && agentsWithProvider.length > 0 && (
        <div style={{ display: 'flex', flexShrink: 0 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: T.gradientPrimary || T.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: '#fff',
            border: `2px solid ${T.bgCard}`, zIndex: 10,
          }}>
            {(user.value?.name || user.value?.email || '?')[0].toUpperCase()}
          </div>
          {agentsWithProvider.slice(0, 3).map((a, i) => (
            <div
              key={a.machine_id}
              title={a.agent_name}
              style={{
                width: 28, height: 28, borderRadius: '50%',
                background: `hsl(${(a.agent_name || a.machine_id).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 60%, 40%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: '#fff',
                border: `2px solid ${T.bgCard}`,
                marginLeft: -6, zIndex: 9 - i,
              }}
            >
              {(a.agent_name || a.machine_id).charAt(0).toUpperCase()}
            </div>
          ))}
          {agentsWithProvider.length > 3 && (
            <div style={{
              width: 28, height: 28, borderRadius: '50%', background: T.bgCardHover,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 600, color: T.textMuted, border: `2px solid ${T.bgCard}`, marginLeft: -6,
            }}>+{agentsWithProvider.length - 3}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Modal Overlay ──
function ModalOverlay({ children, onClose }: { children: any; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 20, animation: 'fadeIn 0.15s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.bg, border: `1px solid ${T.border}`, borderRadius: 16,
          width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
          animation: 'slideUp 0.2s ease-out', position: 'relative',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Connection Manager inside Modal ──
function ConnectionManager({
  item, connections, agents, onRefresh,
}: {
  item: CatalogItem;
  connections: ConnectionInfo[];
  agents: AgentInfo[];
  onRefresh: () => void;
}) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const toggle = (key: string) => setExpandedKey(prev => prev === key ? null : key);

  return (
    <div style={{ padding: '0 24px 24px' }}>
      {/* Existing connections */}
      {connections.map(conn => (
        <ConnectionRow
          key={conn.connection_id}
          item={item}
          connection={conn}
          agents={agents}
          expanded={expandedKey === conn.connection_id}
          onToggle={() => toggle(conn.connection_id)}
          onRefresh={onRefresh}
        />
      ))}

      {/* Agent rows */}
      {agents.map(agent => (
        <AgentRow
          key={agent.machine_id}
          item={item}
          agent={agent}
          connections={connections}
          expanded={expandedKey === `agent:${agent.machine_id}`}
          onToggle={() => toggle(`agent:${agent.machine_id}`)}
          onRefresh={onRefresh}
        />
      ))}

      {/* New connection form */}
      {addingNew && (
        <ConnectionRow
          item={item}
          connection={{ connection_id: crypto.randomUUID().slice(0, 13), keys: {} }}
          agents={agents}
          expanded={true}
          onToggle={() => setAddingNew(false)}
          onRefresh={() => { setAddingNew(false); onRefresh(); }}
          isNew
        />
      )}

      {/* + Add connection */}
      <Button
        variant="secondary" size="sm" fullWidth
        onClick={() => setAddingNew(true)}
        style={{ border: `1px dashed ${T.border}`, background: 'transparent', marginTop: 8 }}
      >
        + Adicionar conta
      </Button>
    </div>
  );
}

// ── Connection Row (accordion) ──
function ConnectionRow({
  item, connection, agents, expanded, onToggle, onRefresh, isNew,
}: {
  item: CatalogItem;
  connection: ConnectionInfo;
  agents: AgentInfo[];
  expanded: boolean;
  onToggle: () => void;
  onRefresh: () => void;
  isNew?: boolean;
}) {
  const [editingField, setEditingField] = useState<string | null>(isNew ? '__all__' : null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [displayName, setDisplayName] = useState(connection.display_name || '');
  const [savingName, setSavingName] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [copyingField, setCopyingField] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fields = parseFields(item);
  const accountKeys = connection.keys;
  const keyPreviews = Object.entries(accountKeys).map(([k, v]) => `${k}: ${v.preview}`).join(', ');

  const handleCopyAccountKey = async (keyName: string) => {
    setCopyingField(keyName);
    try {
      const res = await api.get(`/integrations/${item.id}/account-key?key_name=${encodeURIComponent(keyName)}&connection_id=${encodeURIComponent(connection.connection_id)}`) as any;
      if (res?.value) {
        await navigator.clipboard.writeText(res.value);
        setCopiedField(keyName);
        setTimeout(() => setCopiedField(null), 2000);
      }
    } catch (e) { console.error('Copy failed', e); }
    finally { setCopyingField(null); }
  };

  const handleSave = async (keyName: string, value: string) => {
    if (!value.trim()) return;
    setSaving(true); setSaveResult(null);
    try {
      await api.post('/integrations/queue', {
        provider: item.id, key_name: keyName, value: value.trim(),
        target_type: 'account', connection_id: connection.connection_id,
      });
      setSaveResult({ ok: true, text: 'Salvo com sucesso' });
      setEditingField(null); setFieldValues({});
      onRefresh();
    } catch (e: any) { setSaveResult({ ok: false, text: e.message || 'Erro ao salvar' }); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await api.post(`/integrations/${item.id}/test`, { connection_id: connection.connection_id }) as any;
      setTestResult({ valid: res.valid !== false, message: res.message || (res.valid !== false ? 'Conexao OK' : 'Falhou') });
    } catch (e: any) { setTestResult({ valid: false, message: e.message || 'Erro no teste' }); }
    finally { setTesting(false); }
  };

  const handleCopy = async (direction: string, machineId: string) => {
    setCopying(true);
    try {
      await api.post(`/integrations/${item.id}/copy`, { direction, machine_id: machineId, connection_id: connection.connection_id });
      setSaveResult({ ok: true, text: `Copiado!` });
      onRefresh();
    } catch (e: any) { setSaveResult({ ok: false, text: e.message || 'Erro' }); }
    finally { setCopying(false); }
  };

  const handleSaveDisplayName = async () => {
    setSavingName(true);
    try {
      await api.put(`/integrations/${item.id}/display-name`, { display_name: displayName.trim() || null, connection_id: connection.connection_id });
      setSaveResult({ ok: true, text: 'Nome salvo' });
      onRefresh();
    } catch {} finally { setSavingName(false); }
  };

  const handleDelete = async () => {
    if (!confirm('Remover esta conexao e todas as credenciais?')) return;
    setDeleting(true);
    try {
      await api.delete(`/integrations/${item.id}/connection/${connection.connection_id}`);
      onRefresh();
    } catch (e: any) { setSaveResult({ ok: false, text: e.message || 'Erro ao remover' }); }
    finally { setDeleting(false); }
  };

  return (
    <div style={{ marginBottom: 6 }}>
      {/* Row header */}
      <div onClick={onToggle} style={{
        padding: '12px 16px', borderRadius: expanded ? '10px 10px 0 0' : 10, cursor: 'pointer',
        background: expanded ? T.accentBg : T.bgSubtle, border: `1px solid ${expanded ? T.accent : T.border}`,
        borderBottom: expanded ? `1px solid ${T.accent}` : undefined,
        transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
        onMouseEnter={(e: any) => { if (!expanded) e.currentTarget.style.borderColor = T.accent; }}
        onMouseLeave={(e: any) => { if (!expanded) e.currentTarget.style.borderColor = T.border; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
              {connection.display_name || (connection.connection_id === 'default' ? 'Conta Principal' : isNew ? 'Nova conexao' : `Conta ${connection.connection_id.slice(0, 8)}`)}
            </div>
            {!expanded && keyPreviews && (
              <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2, fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{keyPreviews}</div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: Object.keys(accountKeys).length > 0 ? T.success : T.textPrimary }} />
          <span style={{ color: T.textMuted, fontSize: 14, transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}>›</span>
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div style={{
          padding: 16, borderRadius: '0 0 10px 10px',
          background: T.accentBg, border: `1px solid ${T.accentBg}`, borderTop: 'none',
        }}>
          {/* Display name */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: T.textSecondary, fontWeight: 600 }}>Nome da conexao</label>
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <div style={{ flex: 1 }}>
                <Input value={displayName} onChange={setDisplayName} placeholder="Ex: Conta Principal" style={{ padding: '6px 10px', fontSize: 12 }} />
              </div>
              {displayName !== (connection.display_name || '') && (
                <Button variant="secondary" size="xs" onClick={handleSaveDisplayName} disabled={savingName} loading={savingName}>Salvar</Button>
              )}
            </div>
          </div>

          {/* Fields */}
          {fields.map(field => {
            const existing = accountKeys[field.key];
            const isEditing = editingField === field.key || editingField === '__all__';
            const val = fieldValues[field.key] || '';
            const validation = validateTokenFormat(val);
            return (
              <div key={field.key} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: 11, color: T.textSecondary, fontWeight: 600 }}>{field.label}</label>
                  {existing?.updated_at && <span style={{ fontSize: 9, color: T.textMuted }}>{new Date(existing.updated_at).toLocaleDateString('pt-BR')}</span>}
                </div>
                {isEditing ? (
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <div style={{ flex: 1 }}>
                      <Input value={val} onChange={(v) => setFieldValues({ ...fieldValues, [field.key]: v })}
                        placeholder={field.placeholder} autoFocus
                        style={{ padding: '6px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                          border: `1px solid ${validation === 'valid' ? T.success : validation === 'invalid' ? T.danger : T.borderHover}`,
                        }} />
                    </div>
                    <Button variant="success" size="xs" onClick={() => handleSave(field.key, val)} disabled={saving || !val.trim()} loading={saving}>Salvar</Button>
                    {editingField !== '__all__' && (
                      <Button variant="ghost" size="xs" onClick={() => { setEditingField(null); setFieldValues({}); }}>x</Button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <div style={{
                      flex: 1, padding: '7px 10px', borderRadius: 6, fontSize: 12,
                      background: T.bg, border: `1px solid ${T.border}`,
                      color: existing ? T.text : T.textMuted, fontFamily: "'JetBrains Mono', monospace",
                    }}>{existing ? existing.preview : 'Nao configurado'}</div>
                    {existing && (
                      <Button variant="ghost" size="xs" onClick={() => handleCopyAccountKey(field.key)} disabled={copyingField === field.key}
                        style={{ border: `1px solid ${copiedField === field.key ? T.success : T.borderHover}`, background: copiedField === field.key ? T.successBg : T.bgCard, color: copiedField === field.key ? T.success : T.textSecondary }}>
                        {copyingField === field.key ? '...' : copiedField === field.key ? 'Copiado!' : 'Copiar'}
                      </Button>
                    )}
                    <Button variant="secondary" size="xs" onClick={() => setEditingField(field.key)}>{existing ? 'Editar' : 'Adicionar'}</Button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Results */}
          {saveResult && (
            <div style={{ fontSize: 11, padding: '6px 10px', borderRadius: 6, marginBottom: 8,
              background: saveResult.ok ? T.successBg : T.dangerBg, color: saveResult.ok ? T.success : T.danger,
            }}>{saveResult.text}</div>
          )}
          {testResult && (
            <div style={{ fontSize: 11, padding: '6px 10px', borderRadius: 6, marginBottom: 8,
              background: testResult.valid ? T.successBg : T.dangerBg, color: testResult.valid ? T.success : T.danger,
            }}>{testResult.message}</div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
            {Object.keys(accountKeys).length > 0 && (
              <Button variant="success" size="xs" onClick={handleTest} disabled={testing} loading={testing}>
                {testing ? 'Testando...' : 'Testar'}
              </Button>
            )}
            {agents.map(a => (
              <Button key={a.machine_id} variant="secondary" size="xs" onClick={() => handleCopy('account_to_machine', a.machine_id)} disabled={copying}>
                Copiar p/ {a.agent_name || a.machine_id.slice(0, 8)}
              </Button>
            ))}
            {!isNew && connection.connection_id !== 'default' && (
              <Button variant="danger" size="xs" onClick={handleDelete} disabled={deleting} loading={deleting} style={{ marginLeft: 'auto' }}>Remover</Button>
            )}
          </div>

          {/* Help */}
          {item.how_to_get && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 11, color: T.accent, cursor: 'pointer' }}>Como conseguir credenciais?</summary>
              <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: 6, background: T.accentBg, fontSize: 11, color: T.textSecondary, lineHeight: 1.5 }}>
                {item.how_to_get}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ── Agent Row (accordion) ──
function AgentRow({
  item, agent, connections, expanded, onToggle, onRefresh,
}: {
  item: CatalogItem;
  agent: AgentInfo;
  connections: ConnectionInfo[];
  expanded: boolean;
  onToggle: () => void;
  onRefresh: () => void;
}) {
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const agentInteg = agent.integrations.find(i => i.provider?.toLowerCase() === item.id?.toLowerCase());
  const hasCredentials = !!agentInteg && agentInteg.status === 'active';
  const hasEnvKeys = (agent.env_keys || []).some(k => parseFields(item).some(f => f.key === k));
  const statusColor = hasCredentials ? T.success : (agentInteg || hasEnvKeys) ? T.warning : T.textPrimary;
  const statusLabel = hasCredentials ? 'active' : hasEnvKeys ? 'Configurado (.env)' : agentInteg ? agentInteg.status : 'Nao configurado';

  const handleCopy = async (direction: string, machineId: string, connId?: string) => {
    setCopying(true);
    try {
      await api.post(`/integrations/${item.id}/copy`, { direction, machine_id: machineId, connection_id: connId });
      setResult({ ok: true, text: 'Copiado!' }); onRefresh();
    } catch (e: any) { setResult({ ok: false, text: e.message || 'Erro' }); }
    finally { setCopying(false); }
  };

  const handleCopyKey = async (keyName: string) => {
    try {
      const res = await api.get(`/integrations/${item.id}/machine-key?machine_id=${agent.machine_id}&key_name=${encodeURIComponent(keyName)}`);
      const val = (res as any)?.data?.value || (res as any)?.value;
      if (val) { await navigator.clipboard.writeText(val); setCopiedKey(keyName); setTimeout(() => setCopiedKey(null), 2000); }
    } catch (e: any) { setResult({ ok: false, text: e.message || 'Erro ao copiar' }); }
  };

  const handleSaveKey = async (keyName: string) => {
    if (!editValue.trim()) return;
    setSaving(true);
    try {
      await api.put(`/integrations/${item.id}/machine-key`, { machine_id: agent.machine_id, key_name: keyName, value: editValue });
      setEditingKey(null); setEditValue(''); onRefresh();
    } catch (e: any) { setResult({ ok: false, text: e.message || 'Erro ao salvar' }); }
    finally { setSaving(false); }
  };

  const fields = parseFields(item);

  return (
    <div style={{ marginBottom: 6 }}>
      <div onClick={onToggle} style={{
        padding: '12px 16px', borderRadius: expanded ? '10px 10px 0 0' : 10, cursor: 'pointer',
        background: expanded ? T.accentBg : T.bgSubtle, border: `1px solid ${expanded ? T.accent : T.border}`,
        borderBottom: expanded ? `1px solid ${T.accent}` : undefined,
        transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
        onMouseEnter={(e: any) => { if (!expanded) e.currentTarget.style.borderColor = T.accent; }}
        onMouseLeave={(e: any) => { if (!expanded) e.currentTarget.style.borderColor = T.border; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 24, height: 24, borderRadius: '50%',
            background: `hsl(${(agent.agent_name || agent.machine_id).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 60%, 40%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff',
          }}>{(agent.agent_name || agent.machine_id).charAt(0).toUpperCase()}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{agent.agent_name || agent.machine_id.slice(0, 12)}</div>
            <div style={{ fontSize: 10, color: statusColor, marginTop: 2 }}>{statusLabel}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
          <span style={{ color: T.textMuted, fontSize: 14, transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}>›</span>
        </div>
      </div>

      {expanded && (
        <div style={{
          padding: 16, borderRadius: '0 0 10px 10px',
          background: T.accentBg, border: `1px solid ${T.accentBg}`, borderTop: 'none',
        }}>
          {/* Credential fields */}
          {(() => {
            const agentKeys = agent.credential_previews?.[item.id] || {};
            return fields.map(field => {
              const existing = agentKeys[field.key] || ((agent.env_keys || []).includes(field.key) ? { preview: 'Configurado (.env)', updated_at: '' } : undefined);
              return (
                <div key={field.key} style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, color: T.textSecondary, fontWeight: 600 }}>{field.label}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <div style={{
                      flex: 1, padding: '7px 10px', borderRadius: 6, fontSize: 12,
                      background: T.bg, border: `1px solid ${T.border}`,
                      color: existing ? T.text : T.textMuted, fontFamily: "'JetBrains Mono', monospace",
                    }}>{existing ? existing.preview : 'Nao configurado'}</div>
                    {existing && existing.preview !== 'Configurado (.env)' && (
                      <Button variant="ghost" size="xs" onClick={() => handleCopyKey(field.key)}
                        style={{ color: copiedKey === field.key ? T.success : T.textSecondary }}>
                        {copiedKey === field.key ? 'Copiado!' : 'Copiar'}
                      </Button>
                    )}
                    <Button variant={editingKey === field.key ? 'secondary' : 'ghost'} size="xs"
                      onClick={() => { setEditingKey(editingKey === field.key ? null : field.key); setEditValue(''); }}>Editar</Button>
                  </div>
                  {editingKey === field.key && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <div style={{ flex: 1 }}>
                        <Input value={editValue} onChange={setEditValue} placeholder="Novo valor..." autoFocus
                          style={{ padding: '7px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }} />
                      </div>
                      <Button variant="success" size="xs" onClick={() => handleSaveKey(field.key)} disabled={saving || !editValue.trim()} loading={saving}>Salvar</Button>
                      <Button variant="ghost" size="xs" onClick={() => { setEditingKey(null); setEditValue(''); }}>X</Button>
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {result && (
            <div style={{ fontSize: 11, padding: '6px 10px', borderRadius: 6, marginBottom: 8,
              background: result.ok ? T.successBg : T.dangerBg, color: result.ok ? T.success : T.danger,
            }}>{result.text}</div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {connections.filter(c => Object.keys(c.keys).length > 0).map(conn => (
              <Button key={conn.connection_id} variant="secondary" size="xs"
                onClick={() => handleCopy('account_to_machine', agent.machine_id, conn.connection_id)} disabled={copying}>
                Copiar da conta{connections.length > 1 ? ` "${conn.display_name || 'Principal'}"` : ''}
              </Button>
            ))}
            {hasCredentials && (
              <Button variant="ghost" size="xs" onClick={() => handleCopy('machine_to_account', agent.machine_id)} disabled={copying}>
                Copiar p/ conta
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Integration Detail Modal ──
function IntegrationModal({
  item, connections, agents, account, onClose, onRefresh,
}: {
  item: CatalogItem;
  connections: ConnectionInfo[];
  agents: AgentInfo[];
  account: Record<string, AccountKeys>;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const status = getStatus(item.id, account, agents);
  const hasConnections = connections.some(c => Object.keys(c.keys).length > 0);
  const authType = item.auth_type || (item.id === 'github' ? 'OAuth' : 'API Key');
  const agentsWithProvider = agents.filter(a =>
    a.integrations.some(i => i.provider?.toLowerCase() === item.id?.toLowerCase())
  );

  return (
    <ModalOverlay onClose={onClose}>
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
          color: T.textMuted, fontSize: 20, cursor: 'pointer', padding: 4, lineHeight: 1,
          zIndex: 1,
        }}
      >x</button>

      {/* Header — centered icon + name + description */}
      <div style={{ padding: '32px 24px 20px', textAlign: 'center' }}>
        {/* Icon */}
        {item.svg_path ? (
          <div style={{
            width: 64, height: 64, borderRadius: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: `${item.color}15`, border: `1px solid ${item.color}25`, marginBottom: 16,
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill={item.color || T.text}><path d={item.svg_path} /></svg>
          </div>
        ) : (
          <div style={{
            width: 64, height: 64, borderRadius: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: `${item.color}15`, border: `1px solid ${item.color}25`, marginBottom: 16,
            fontSize: 28, fontWeight: 700, color: item.color,
          }}>
            {item.label.charAt(0)}
          </div>
        )}

        <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 8 }}>{item.label}</div>
        <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.5, maxWidth: 360, margin: '0 auto' }}>
          {item.description || `Gerencie suas credenciais de ${item.label}`}
        </div>

        {/* Status + auth type badges */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
          <Badge variant="info" color={T.accent}>{authType}</Badge>
          {status === 'connected' && <Badge variant="success">Conectado</Badge>}
          {status === 'partial' && <Badge variant="warning">Parcial</Badge>}
        </div>

        {/* Agentes conectados */}
        {agentsWithProvider.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>
              {agentsWithProvider.length} agente{agentsWithProvider.length !== 1 ? 's' : ''} conectado{agentsWithProvider.length !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 320, margin: '0 auto' }}>
              {agentsWithProvider.map(a => {
                const name = a.agent_name || a.machine_id.slice(0, 8);
                const hue = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
                return (
                  <div
                    key={a.machine_id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px',
                      borderRadius: 8, background: `${T.bgHover || 'rgba(255,255,255,0.05)'}`,
                      transition: 'background 0.15s',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      background: `hsl(${hue}, 60%, 40%)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, color: '#fff',
                    }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {name}
                      </div>
                      <div style={{ fontSize: 10, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.machine_id}
                      </div>
                    </div>
                    <div style={{
                      width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                      background: '#34d399',
                    }} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Conta (account-level connection) */}
        {hasConnections && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>
              Conta
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 320, margin: '0 auto' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px',
                borderRadius: 8, background: `${T.bgHover || 'rgba(255,255,255,0.05)'}`,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: T.gradientPrimary || T.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: '#fff',
                }}>
                  {(user.value?.name || user.value?.email || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                    {user.value?.name || user.value?.email?.split('@')[0] || 'Minha conta'}
                  </div>
                  <div style={{ fontSize: 10, color: T.textMuted }}>Nível conta</div>
                </div>
                <div style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: '#34d399' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* + Conectar (primary CTA) */}
      {!showDetails && (
        <div style={{ padding: '0 24px 16px', textAlign: 'center' }}>
          {item.id === 'github' ? (
            <Button
              variant="primary"
              onClick={() => { window.location.href = '/api/v1/integrations/github/oauth'; }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              + Conectar via OAuth
            </Button>
          ) : (
            <Button variant="primary" onClick={() => setShowDetails(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              + Conectar
            </Button>
          )}
        </div>
      )}

      {/* Mostrar detalhes toggle */}
      {!showDetails && hasConnections && (
        <div style={{ padding: '0 24px 24px', textAlign: 'center' }}>
          <button
            onClick={() => setShowDetails(true)}
            style={{
              background: 'none', border: 'none', color: T.textMuted, fontSize: 13,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            Mostrar detalhes <span style={{ fontSize: 10 }}>▼</span>
          </button>
        </div>
      )}

      {/* Detail section (connections + agents) */}
      {showDetails && (
        <>
          <div style={{ height: 1, background: T.border, margin: '0 24px 16px' }} />
          <ConnectionManager item={item} connections={connections} agents={agents} onRefresh={onRefresh} />
        </>
      )}

      {/* Docs link */}
      {item.docs_url && (
        <div style={{ padding: '0 24px 20px', textAlign: 'center' }}>
          <a href={item.docs_url} target="_blank" rel="noopener" style={{ fontSize: 12, color: T.accent, textDecoration: 'none' }}>
            Documentacao →
          </a>
        </div>
      )}
    </ModalOverlay>
  );
}

// ── Main Page ──
export function IntegrationsPage(_props: { path?: string }) {
  const [catalogData, setCatalogData] = useState<CatalogItem[] | null>(null);
  const [statusData, setStatusData] = useState<{ account: Record<string, AccountKeys>; agents: AgentInfo[] } | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todos');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);

  const loadCatalog = useCallback(async () => {
    try {
      const catRes = await api.get('/integrations-catalog') as any;
      setCatalogData(catRes?.integrations || catRes?.catalog || []);
    } catch { setCatalogData([]); }
    finally { setCatalogLoading(false); }
  }, []);

  const loadFull = useCallback(async () => {
    try {
      const res = await api.get('/integrations/full') as any;
      if (res?.catalog?.length) setCatalogData(res.catalog);
      setStatusData({ account: res?.account || {}, agents: res?.agents || [] });
    } catch { setStatusData({ account: {}, agents: [] }); }
    finally { setStatusLoading(false); }
  }, []);

  const loadData = useCallback(async () => {
    setStatusLoading(true);
    await loadFull();
  }, [loadFull]);

  useEffect(() => { loadCatalog(); loadFull(); }, []);

  const catalog = catalogData || [];
  const account = statusData?.account || {};
  const agents = statusData?.agents || [];

  const filtered = useMemo(() => catalog.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!c.label.toLowerCase().includes(q) && !(c.description || '').toLowerCase().includes(q) && !c.id.toLowerCase().includes(q)) return false;
    }
    if (categoryFilter !== 'Todos' && c.category !== categoryFilter) return false;
    if (statusFilter !== 'all') {
      const s = getStatus(c.id, account, agents);
      if (statusFilter === 'connected' && s !== 'connected') return false;
      if (statusFilter === 'partial' && s !== 'partial') return false;
      if (statusFilter === 'disconnected' && s !== 'disconnected') return false;
    }
    return true;
  }), [catalog, account, agents, search, categoryFilter, statusFilter]);

  const availableCategories = useMemo(() => ['Todos', ...Array.from(new Set(catalog.map(c => c.category).filter(Boolean)))], [catalog]);

  // Separate connected vs available
  const connected = useMemo(() => filtered.filter(c => getStatus(c.id, account, agents) !== 'disconnected'), [filtered, account, agents]);
  const available = useMemo(() => filtered.filter(c => getStatus(c.id, account, agents) === 'disconnected'), [filtered, account, agents]);

  const loading = catalogLoading;

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0, letterSpacing: -0.5 }}>Integracoes</h1>
          <p style={{ fontSize: 13, color: T.textSecondary, margin: '4px 0 0' }}>
            Conecte suas ferramentas favoritas aos seus agentes.
          </p>
        </div>
        <div style={{ maxWidth: 280 }}>
          <SearchInput placeholder="Pesquisar..." onSearch={setSearch} debounceMs={200} />
        </div>
      </div>

      {/* Category + Status filters */}
      {!loading && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24, alignItems: 'center' }}>
          <FilterChips
            options={availableCategories.map(cat => ({ key: cat, label: cat }))}
            selected={categoryFilter}
            onChange={setCategoryFilter}
            size="md"
          />
          <div style={{ marginLeft: 'auto' }}>
            <FilterChips
              options={[
                { key: 'all', label: 'Todos' },
                { key: 'connected', label: 'Conectados' },
                { key: 'disconnected', label: 'Disponiveis' },
              ]}
              selected={statusFilter}
              onChange={setStatusFilter}
              size="sm"
            />
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
          {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      )}

      {/* Connected section */}
      {!loading && connected.length > 0 && statusFilter !== 'disconnected' && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
            Conectados
          </div>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', marginBottom: 32 }}>
            {connected.map(item => (
              <IntegrationGridCard
                key={item.id}
                item={item}
                status={getStatus(item.id, account, agents)}
                agents={agents}
                account={account}
                statusLoading={statusLoading}
                onClick={() => setSelectedItem(item)}
              />
            ))}
          </div>
        </>
      )}

      {/* Available section */}
      {!loading && available.length > 0 && statusFilter !== 'connected' && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
            Aplicativos
          </div>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
            {available.map(item => (
              <IntegrationGridCard
                key={item.id}
                item={item}
                status={getStatus(item.id, account, agents)}
                agents={agents}
                account={account}
                statusLoading={statusLoading}
                onClick={() => setSelectedItem(item)}
              />
            ))}
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '60px 24px',
          background: T.bgCard, borderRadius: 14, border: `1px solid ${T.border}`,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>
            {search ? 'Nenhuma integracao encontrada' : 'Nenhuma integracao disponivel'}
          </div>
          <div style={{ fontSize: 13, color: T.textSecondary }}>
            {search ? `Nenhum resultado para "${search}"` : 'O catalogo de integracoes ainda esta vazio.'}
          </div>
        </div>
      )}

      {/* Modal */}
      {selectedItem && (
        <IntegrationModal
          item={selectedItem}
          connections={getConnections(selectedItem.id, account)}
          agents={agents}
          account={account}
          onClose={() => setSelectedItem(null)}
          onRefresh={loadData}
        />
      )}
    </div>
  );
}
